import { Link } from "react-router-dom";
import type { DocItem } from "@/lib/types";

// 규정 코드별 아이콘
const ICONS: Record<string, string> = {
  rule: "📜",
  bylaw: "📋",
  mutual: "🤝",
};

export function ruleIcon(code?: string): string {
  return (code && ICONS[code]) || "📄";
}

export function formatRevdate(v?: string | null): string {
  if (!v) return "";
  const s = String(v).slice(0, 10).replace(/-/g, ".");
  if (!s || s === "0000.00.00") return "";
  return `${s} 개정`;
}

interface RuleCardProps {
  doc: DocItem;
}

// 메인 홈 · /rules 목록에서 함께 쓰는 규정 카드
export default function RuleCard({ doc }: RuleCardProps) {
  // 본문은 short(줄바꿈 없는 140자)를 우선 사용하고, 없으면 summary 로 대체한다.
  const body = String(doc.short || doc.summary || "");
  const icon = ruleIcon(doc.code);
  const ready = Number(doc.has ?? 1) !== 0;
  const rev = formatRevdate(doc.revdate);

  const base =
    "flex h-full flex-col rounded-xl border bg-background-50 p-5 md:p-6 transition-colors";

  if (!ready) {
    return (
      <div className={`${base} border-background-200`}>
        <div className="flex items-center justify-between gap-3">
          <span
            className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-100 text-2xl leading-none opacity-60"
            aria-hidden="true"
          >
            {icon}
          </span>
          <span className="whitespace-nowrap rounded-full bg-background-200 px-2.5 py-0.5 text-[11px] font-semibold text-foreground-500">
            준비중
          </span>
        </div>
        <h3 className="mt-4 font-heading text-lg font-bold text-foreground-700">
          {doc.name}
        </h3>
        <p className="mt-2 text-sm leading-relaxed text-foreground-400">
          내용을 준비하고 있습니다.
        </p>
      </div>
    );
  }

  return (
    <Link
      to={`/rules/${doc.code}`}
      className={`${base} border-background-200 hover:border-primary-300 hover:bg-background-100`}
    >
      <div className="flex items-center justify-between gap-3">
        <span
          className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-100 text-2xl leading-none"
          aria-hidden="true"
        >
          {icon}
        </span>
        {rev && (
          <span className="whitespace-nowrap text-xs text-foreground-500">{rev}</span>
        )}
      </div>
      <h3 className="mt-4 font-heading text-lg font-bold text-foreground-950">
        {doc.name}
      </h3>
      <p className="mt-2 line-clamp-3 text-sm leading-relaxed text-foreground-600">
        {body}
      </p>
      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-700">
        자세히 보기
        <i className="ri-arrow-right-line" aria-hidden="true"></i>
      </span>
    </Link>
  );
}